// Security utilities for the application

// Generate CSRF token
export const generateCSRFToken = () => {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
};

// Validate CSRF token with timing-safe comparison
export const validateCSRFToken = (token, storedToken) => {
  if (!token || !storedToken) return false;

  // Convert to same length to prevent timing attacks
  const tokenBytes = new TextEncoder().encode(token);
  const storedBytes = new TextEncoder().encode(storedToken);

  if (tokenBytes.length !== storedBytes.length) {
    return false;
  }

  // Constant-time comparison
  let result = 0;
  for (let i = 0; i < tokenBytes.length; i++) {
    result |= tokenBytes[i] ^ storedBytes[i];
  }

  return result === 0;
};

// Set CSRF token in session storage
export const setCSRFToken = () => {
  const token = generateCSRFToken();
  sessionStorage.setItem('csrf_token', token);
  return token;
};

// Get CSRF token from session storage
export const getCSRFToken = () => {
  return sessionStorage.getItem('csrf_token');
};

// Sanitize HTML input to prevent XSS
export const sanitizeHTML = (input) => {
  if (typeof input !== 'string') return input;

  const div = document.createElement('div');
  div.textContent = input;
  return div.innerHTML
    .replace(/&lt;script[^&]*&gt;.*?&lt;\/script&gt;/gi, '')
    .replace(/javascript:/gi, '')
    .replace(/on\w+\s*=/gi, '');
};

// Validate URL to prevent SSRF
export const validateURL = (url) => {
  try {
    const urlObj = new URL(url);

    // Only allow HTTPS in production
    if (process.env.NODE_ENV === 'production' && urlObj.protocol !== 'https:') {
      return false;
    }

    // In development, allow localhost and local IPs
    if (process.env.NODE_ENV !== 'production') {
      return true;
    }

    // Block private IP ranges in production only
    const hostname = urlObj.hostname;
    const privateRanges = [
      /^127\./,
      /^10\./,
      /^172\.(1[6-9]|2[0-9]|3[0-1])\./,
      /^192\.168\./,
      /^localhost$/i,
      /^0\.0\.0\.0$/
    ];

    return !privateRanges.some(range => range.test(hostname));
  } catch {
    return false;
  }
};

// Rate limiting helper
const rateLimitMap = new Map();

export const checkRateLimit = (key, maxRequests = 10, windowMs = 60000) => {
  const now = Date.now();
  const windowStart = now - windowMs;

  if (!rateLimitMap.has(key)) {
    rateLimitMap.set(key, []);
  }

  const requests = rateLimitMap.get(key);

  // Remove old requests outside the window
  const validRequests = requests.filter(timestamp => timestamp > windowStart);

  if (validRequests.length >= maxRequests) {
    return false; // Rate limit exceeded
  }

  validRequests.push(now);
  rateLimitMap.set(key, validRequests);

  return true; // Request allowed
};

// Input validation helpers
export const validateEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

export const validatePhone = (phone) => {
  const phoneRegex = /^[\+]?[1-9][\d]{0,15}$/;
  return phoneRegex.test(phone.replace(/[\s\-\(\)]/g, ''));
};

export const validatePassword = (password) => {
  // At least 8 characters, 1 uppercase, 1 lowercase, 1 number
  const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)[a-zA-Z\d@$!%*?&]{8,}$/;
  return passwordRegex.test(password);
};

// Content Security Policy helper - DISABLED for development
export const setCSPHeaders = () => {
  // CSP completely disabled in development to allow local images
  if (process.env.NODE_ENV === 'production') {
    const meta = document.createElement('meta');
    meta.httpEquiv = 'Content-Security-Policy';
    meta.content = "default-src 'self'; script-src 'self' 'unsafe-inline' 'unsafe-eval'; style-src 'self' 'unsafe-inline' https://fonts.googleapis.com; img-src 'self' data: https: http: https://i.imgur.com; font-src 'self' https: https://fonts.gstatic.com; connect-src 'self' https: http: wss:;";
    document.head.appendChild(meta);
  }
  console.log('🔓 CSP Headers:', process.env.NODE_ENV === 'production' ? 'ENABLED' : 'DISABLED');
};